import { CounselType, Room, Teller, User } from '@raysee/schema';
import { removeDecorationTags, renderTimeSimple } from '@raysee/utility';
import dayjs from 'dayjs';
import Image from 'next/image';
import { Link } from '../Link';

interface Props {
  items: {
    room: Pick<
      Room,
      | 'id'
      | 'tellerId'
      | 'planId'
      | 'latestMessage'
      | 'updateTime'
      | 'tellerViewedTime'
      | 'userSentTime'
      | 'userViewedTime'
      | 'tellerSentTime'
    > & {
      counselType?: CounselType;
    };
    user: User;
    teller: Pick<Teller, 'id' | 'name' | 'imageUrl'>;
  }[];
  role: 'teller' | 'user';
  isPhone?: boolean;
}

export const RoomList = ({ items, role, isPhone }: Props) => (
  <ul>
    {items
      .filter(({ user }) => !!user)
      .map(({ room, user, teller }, i) => (
        <li
          className={`py-4 ${
            existNewMessage(role, room) ? 'bg-brand/20 mx-[calc(50%-50vw)] px-[calc(50vw-50%)]' : ''
          }`}
          key={i}
        >
          <Link href={getHref(role, room, teller, isPhone)} legacyBehavior>
            <a className="flex items-center">
              <div className="h-16 w-16">
                {role === 'teller' && user.imageUrl?.length > 0 && (
                  <Image
                    src={user.imageUrl}
                    width="64"
                    height="64"
                    className="rounded-full"
                    alt={user.name}
                  />
                )}
                {role === 'user' && teller.imageUrl && (
                  <Image
                    src={teller.imageUrl}
                    width="64"
                    height="64"
                    className="rounded-full"
                    alt={teller.name}
                  />
                )}
              </div>
              <div className="mx-4 flex-1 truncate">
                <p className="mb-2 text-gray-400">
                  {role === 'teller' ? user.name + 'さん' : teller.name + '先生'}
                </p>
                <div className="truncate text-black">
                  {removeDecorationTags(room.latestMessage)}
                </div>
              </div>
              <div className="flex flex-col items-end">
                <p className="mb-2 text-gray-400">{renderTimeSimple(new Date(room.updateTime))}</p>
              </div>
            </a>
          </Link>
        </li>
      ))}
  </ul>
);

const existNewMessage = (
  role: 'teller' | 'user',
  room: Pick<
    Room,
    | 'id'
    | 'tellerId'
    | 'planId'
    | 'latestMessage'
    | 'updateTime'
    | 'tellerViewedTime'
    | 'userSentTime'
    | 'userViewedTime'
    | 'tellerSentTime'
  > & {
    counselType?: CounselType;
  },
) => {
  if (role === 'teller')
    return (
      !room.tellerViewedTime ||
      (room.tellerViewedTime &&
        room.userSentTime &&
        room.latestMessage !== '鑑定終了' &&
        room.userSentTime > room.tellerSentTime)
    );
  else
    return (
      room.userViewedTime &&
      room.tellerSentTime &&
      dayjs(room.userViewedTime).isBefore(room.tellerSentTime)
    );
};

const getHref = (
  role: 'teller' | 'user',
  room: Pick<
    Room,
    | 'id'
    | 'tellerId'
    | 'planId'
    | 'latestMessage'
    | 'updateTime'
    | 'tellerViewedTime'
    | 'userSentTime'
    | 'userViewedTime'
    | 'tellerSentTime'
  > & {
    counselType?: CounselType;
  },
  teller: Pick<Teller, 'id' | 'name' | 'imageUrl'>,
  isPhone?: boolean,
) => {
  let href = '';
  if (room.planId !== '') href = role === 'teller' ? `/rooms/${room.id}` : `/rooms/${teller.id}`;
  else {
    const matchedText = room.id.match(/^([A-Z1-9]+?)_/);
    if (!matchedText || matchedText[1] === 'LINE') href = '/counsel/room';
    else {
      if (room.counselType === 'new_year_2024') href = '/counsel/new_year_2024';
      else href = `/counsel/${matchedText[1].toLowerCase()}`;
    }
  }

  if (isPhone) {
    href = '/phone' + href;
  }

  return href;
};
